import React, { useState, useMemo } from "react";
import {
  Button,
  TextField,
  Box,
  Container,
  Typography,
  Alert,
  Snackbar,
  CircularProgress,
  Paper,
} from "@mui/material";
import {
  Mail,
  Dna,
  Activity,
  ArrowLeft,
  CheckCircle,
  AlertCircle,
} from "lucide-react";

// Import types and utilities
import type { ApiError } from "@/types/auth.type";
import { CLINICAL_FONT_FAMILY } from "@/components/shared/constants";
import { container } from "@/container/DependencyContainer";

interface ForgotPasswordPageProps {
  onBackToLogin: () => void;
}

const ForgotPasswordPage: React.FC<ForgotPasswordPageProps> = ({
  onBackToLogin,
}) => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  const isValidEmail = (emailAddress: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(emailAddress);
  };

  // Email validation state
  const emailValidation = useMemo(() => {
    if (!email) {
      return { isValid: false, message: "", status: "empty" };
    }

    if (email.length < 5) {
      return {
        isValid: false,
        message: "Email is too short",
        status: "invalid",
      };
    }

    if (!email.includes("@")) {
      return {
        isValid: false,
        message: "Email must contain @ symbol",
        status: "invalid",
      };
    }

    const atIndex = email.indexOf("@");
    if (atIndex === 0) {
      return {
        isValid: false,
        message: "Email must have text before @",
        status: "invalid",
      };
    }

    if (!email.includes(".")) {
      return {
        isValid: false,
        message: "Email must contain domain extension (.com, .org, etc.)",
        status: "invalid",
      };
    }

    const domainPart = email.substring(atIndex + 1);
    if (domainPart.length < 3) {
      return {
        isValid: false,
        message: "Domain name is too short",
        status: "invalid",
      };
    }

    if (isValidEmail(email)) {
      return {
        isValid: true,
        message: "Email looks good",
        status: "valid",
      };
    }

    return {
      isValid: false,
      message: "Please enter a valid email format",
      status: "invalid",
    };
  }, [email]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccessMessage(null);

    try {
      if (!email) {
        throw new Error("Please enter your email address");
      }

      if (!isValidEmail(email)) {
        throw new Error("Please enter a valid email address");
      }

      // Call the forgot password use case
      await container.forgotPasswordUseCase.execute({ email });

      setSuccessMessage(
        "Password reset instructions have been sent to your email.",
      );
      setEmailSent(true);
      setSnackbarOpen(true);
    } catch (apiError) {
      const errorMessage =
        (apiError as ApiError)?.message ||
        "Failed to send reset instructions. For security, we'll send instructions only if the email is registered.";
      setError(errorMessage);
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background:
          "linear-gradient(135deg, #EBF4FF 0%, #E0F2FE 50%, #EBF4FF 100%)",
        position: "relative",
        overflow: "hidden",
        fontFamily: CLINICAL_FONT_FAMILY,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {/* Background Pattern */}
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          opacity: 0.1,
          "& .dna-1": {
            position: "absolute",
            top: "5rem",
            left: "5rem",
            color: "#3B82F6",
            animation: "pulse 2s infinite",
          },
          "& .activity-1": {
            position: "absolute",
            top: "10rem",
            right: "8rem",
            color: "#14B8A6",
            animation: "pulse 2s infinite 1s",
          },
        }}
      >
        <Dna className="dna-1" size={64} />
        <Activity className="activity-1" size={80} />
      </Box>

      {/* Main Container */}
      <Container
        maxWidth="sm"
        sx={{
          position: "relative",
          zIndex: 10,
          py: 4,
        }}
      >
        {/* Card */}
        <Paper
          elevation={0}
          sx={{
            borderRadius: 3,
            boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1)",
            overflow: "hidden",
            backgroundColor: "white",
          }}
        >
          <Box
            sx={{
              p: 4,
              display: "flex",
              flexDirection: "column",
              gap: 3,
            }}
          >
            {/* Header */}
            <Box>
              <Typography
                variant="h5"
                sx={{
                  fontFamily: CLINICAL_FONT_FAMILY,
                  fontWeight: 600,
                  color: "#1F2937",
                  mb: 0.5,
                }}
              >
                Reset Password
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontFamily: CLINICAL_FONT_FAMILY,
                  color: "#6B7280",
                }}
              >
                Enter your email address to receive password reset instructions
              </Typography>
            </Box>

            {!emailSent ? (
              <>
                {/* Security Info Alert */}
                <Alert
                  severity="info"
                  icon={<Mail size={20} />}
                  sx={{
                    backgroundColor: "#EFF6FF",
                    color: "#1E40AF",
                    border: "1px solid #BFDBFE",
                    borderRadius: 2,
                    "& .MuiAlert-message": {
                      fontFamily: CLINICAL_FONT_FAMILY,
                      fontSize: "0.9rem",
                    },
                  }}
                >
                  For security, we'll send instructions only if the email is
                  registered.
                </Alert>

                {/* Form */}
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    void handleSubmit(e);
                  }}
                >
                  {/* Email Field */}
                  <Box sx={{ mb: 3 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        fontFamily: CLINICAL_FONT_FAMILY,
                        fontWeight: 600,
                        color: "#1F2937",
                        mb: 1,
                      }}
                    >
                      Email Address <span style={{ color: "#DC2626" }}>*</span>
                    </Typography>
                    <TextField
                      fullWidth
                      type="email"
                      placeholder="Enter your registered email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      disabled={loading}
                      error={
                        emailValidation.status === "invalid" && email.length > 0
                      }
                      InputProps={{
                        startAdornment: (
                          <Mail
                            size={20}
                            style={{
                              marginRight: 12,
                              color:
                                emailValidation.status === "valid"
                                  ? "#10B981"
                                  : emailValidation.status === "invalid" &&
                                      email.length > 0
                                    ? "#DC2626"
                                    : "#9CA3AF",
                            }}
                          />
                        ),
                        endAdornment: email ? (
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              ml: 1,
                            }}
                          >
                            {emailValidation.status === "valid" ? (
                              <CheckCircle
                                size={20}
                                style={{ color: "#10B981" }}
                              />
                            ) : emailValidation.status === "invalid" ? (
                              <AlertCircle
                                size={20}
                                style={{ color: "#DC2626" }}
                              />
                            ) : null}
                          </Box>
                        ) : null,
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          fontFamily: CLINICAL_FONT_FAMILY,
                          backgroundColor: "#F9FAFB",
                          border: "1.5px solid",
                          borderColor:
                            emailValidation.status === "valid"
                              ? "#10B981"
                              : emailValidation.status === "invalid" &&
                                  email.length > 0
                                ? "#DC2626"
                                : "#E5E7EB",
                          borderRadius: 1.5,
                          transition: "all 0.3s ease",
                          "&:hover": {
                            borderColor:
                              emailValidation.status === "valid"
                                ? "#059669"
                                : emailValidation.status === "invalid" &&
                                    email.length > 0
                                  ? "#B91C1C"
                                  : "#D1D5DB",
                          },
                          "&.Mui-focused": {
                            backgroundColor: "white",
                            borderColor:
                              emailValidation.status === "valid"
                                ? "#10B981"
                                : emailValidation.status === "invalid" &&
                                    email.length > 0
                                  ? "#DC2626"
                                  : "#3B82F6",
                            boxShadow:
                              emailValidation.status === "valid"
                                ? "0 0 0 3px rgba(16, 185, 129, 0.1)"
                                : emailValidation.status === "invalid" &&
                                    email.length > 0
                                  ? "0 0 0 3px rgba(220, 38, 38, 0.1)"
                                  : "0 0 0 3px rgba(59, 130, 246, 0.1)",
                          },
                        },
                        "& .MuiOutlinedInput-input::placeholder": {
                          color: "#9CA3AF",
                          opacity: 1,
                        },
                      }}
                    />
                    {/* Validation Message */}
                    {email && emailValidation.message && (
                      <Typography
                        variant="caption"
                        sx={{
                          mt: 0.75,
                          fontFamily: CLINICAL_FONT_FAMILY,
                          color:
                            emailValidation.status === "valid"
                              ? "#10B981"
                              : "#DC2626",
                          fontWeight: 500,
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                        }}
                      >
                        {emailValidation.status === "valid" ? (
                          <CheckCircle size={16} style={{ color: "#10B981" }} />
                        ) : (
                          <AlertCircle size={16} style={{ color: "#DC2626" }} />
                        )}
                        {emailValidation.message}
                      </Typography>
                    )}
                  </Box>

                  {/* Send Reset Link Button */}
                  <Button
                    fullWidth
                    type="submit"
                    variant="contained"
                    disabled={loading || !emailValidation.isValid}
                    sx={{
                      py: 1.75,
                      mb: 2,
                      fontFamily: CLINICAL_FONT_FAMILY,
                      fontWeight: 600,
                      fontSize: "1rem",
                      textTransform: "none",
                      background:
                        "linear-gradient(90deg, #0EA5E9 0%, #06B6D4 100%)",
                      color: "white",
                      border: "none",
                      borderRadius: 2,
                      transition: "all 0.3s ease",
                      "&:hover:not(:disabled)": {
                        boxShadow: "0 10px 25px -5px rgba(6, 182, 212, 0.3)",
                        transform: "translateY(-2px)",
                      },
                      "&:disabled": {
                        opacity: 0.6,
                      },
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 1,
                    }}
                  >
                    {loading && <CircularProgress size={20} color="inherit" />}
                    Send Reset Link
                  </Button>
                </form>
              </>
            ) : (
              /* Success State */
              <Box
                sx={{
                  textAlign: "center",
                  py: 2,
                }}
              >
                <Box
                  sx={{
                    width: 64,
                    height: 64,
                    margin: "0 auto",
                    mb: 2,
                    borderRadius: "50%",
                    backgroundColor: "#ECFDF5",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Typography sx={{ fontSize: "2rem" }}>✓</Typography>
                </Box>
                <Typography
                  variant="h6"
                  sx={{
                    fontFamily: CLINICAL_FONT_FAMILY,
                    fontWeight: 600,
                    color: "#1F2937",
                    mb: 1,
                  }}
                >
                  Check your email
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    fontFamily: CLINICAL_FONT_FAMILY,
                    color: "#6B7280",
                    mb: 3,
                  }}
                >
                  We've sent password reset instructions to {email}
                </Typography>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={onBackToLogin}
                  sx={{
                    py: 1.5,
                    fontFamily: CLINICAL_FONT_FAMILY,
                    fontWeight: 600,
                    fontSize: "0.95rem",
                    textTransform: "none",
                    background:
                      "linear-gradient(90deg, #0EA5E9 0%, #06B6D4 100%)",
                    borderRadius: 2,
                  }}
                >
                  Back to Sign In
                </Button>
              </Box>
            )}

            {/* Back to Sign In Link */}
            {!emailSent && (
              <Button
                onClick={onBackToLogin}
                startIcon={<ArrowLeft size={20} />}
                sx={{
                  fontFamily: CLINICAL_FONT_FAMILY,
                  color: "#0EA5E9",
                  textTransform: "none",
                  fontSize: "0.95rem",
                  fontWeight: 500,
                  justifyContent: "center",
                  "&:hover": {
                    backgroundColor: "transparent",
                    textDecoration: "underline",
                  },
                }}
              >
                Back to Sign in
              </Button>
            )}
          </Box>
        </Paper>
      </Container>

      {/* Snackbar for errors and success */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={error ? "error" : "success"}
          sx={{
            fontFamily: CLINICAL_FONT_FAMILY,
            width: "100%",
          }}
        >
          {error ?? successMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ForgotPasswordPage;
